import { CasoData, Consecuencia, FilaPedido, FilaProceso, RegistroOp } from './models/liberacion.models';

export const DETALLE_MIN = 6;
export const DETALLE_MAX = 200;

const ROL: Record<string, string> = { R: 'Recogida', E: 'Entrega' };

const ETAPA: Record<string, { clase: string; texto: string }> = {
  P: { clase: 'info', texto: 'Pendiente' },
  V: { clase: 'warn', texto: 'Viajando' },
  B: { clase: 'err', texto: 'Bloqueado' },
  F: { clase: 'ok', texto: 'Finalizado' },
  L: { clase: 'ok', texto: 'Liberado' }
};

export function validarDetalle(texto: string): string | null {
  const largo = (texto || '').trim().length;
  if (largo < DETALLE_MIN) return `Escribe al menos ${DETALLE_MIN} caracteres.`;
  if (largo > DETALLE_MAX) return `Escribe máximo ${DETALLE_MAX} caracteres.`;
  return null;
}

export function etiquetaEstadoOp(estado: string): { clase: string; texto: string } {
  const e = (estado || '').toUpperCase();
  if (e === 'ANULADO') return { clase: 'err', texto: 'Anulado' };
  return { clase: e === 'FACTURADO' ? 'warn' : 'info', texto: e ? e.charAt(0) + e.slice(1).toLowerCase() : '—' };
}

/** Filas de pedidos SAP del caso. Si recogida y entrega comparten pedido, una sola fila. */
export function filasPedidos(caso: CasoData): FilaPedido[] {
  const estadoDe = (l: CasoData['lados'][0]) =>
    l.calificado === 'N' ? { clase: 'ok', texto: 'Ya liberado' } : (ETAPA[l.etapa] || { clase: 'info', texto: l.etapa });
  const lados = caso.lados;
  const mismo = lados.length > 1 && !!lados[0].num_pedido_sap && lados.every(l => l.num_pedido_sap === lados[0].num_pedido_sap);
  const grupos = mismo ? [lados] : lados.map(l => [l]);
  return grupos.map(g => ({
    rol: mismo ? 'Recogida y entrega' : ROL[g[0].rol],
    sap: g[0].num_pedido_sap,
    hecho: g.every(l => l.estado === 'hecho'),
    motivo: g.length === 1 ? g[0].motivo : null,
    estado: estadoDe(g[0])
  }));
}

/** Filas de "Procesos del caso": solo los talleres traen `procesos`; el resto de casos usa `filasPedidos`. */
export function filasProcesos(caso: CasoData): FilaProceso[] {
  return (caso.procesos || []).map(p => ({
    nombre: p.nombre,
    detalle: (p.num_pedido_sap ? `SAP ${p.num_pedido_sap} · ` : '') + `Cita ${p.cita || '—'}`,
    accion: p.accion === 'liberar' ? 'Se libera el pedido en SAP y se limpia la cita.'
      : p.accion === 'cerrar' ? 'Se cierra el servicio y se limpia su fecha.' : (p.motivo || 'Se omite.'),
    omitido: p.accion === 'omitir',
    estado: ETAPA[p.etapa || ''] || { clase: 'info', texto: p.etapa || 'Sin etapa' }
  }));
}

/** "Qué pasará al liberar" para una OP; null mientras falte elegir la entrega. */
export function consecuenciasOp(registro: RegistroOp | null, totalEntregas: number): Consecuencia[] | null {
  if (!registro) return null;
  const lineas: Consecuencia[] = [
    registro.num_pedido_sap ? { texto: 'SAP concluye el pedido', sap: registro.num_pedido_sap } : { texto: 'SAP concluye el pedido de la OP.' },
    { texto: 'La OP vuelve a «Confirmación de cita».' },
    { texto: 'Se crea una tarea para que el asesor dé una nueva cita.' }
  ];
  if (totalEntregas > 1) lineas.push({ texto: 'Las otras entregas no se modifican.' });
  return lineas;
}

/** "Qué pasará al liberar" para un caso: un efecto por pedido SAP. */
export function consecuenciasCaso(caso: CasoData): Consecuencia[] {
  if (caso.procesos && caso.procesos.length) {
    return caso.procesos.map(p => {
      if (p.accion === 'liberar') return { texto: `SAP libera el pedido de ${p.nombre.toLowerCase()}`, sap: p.num_pedido_sap || undefined };
      if (p.accion === 'cerrar') return { texto: `${p.nombre}: se cierra el servicio y se limpia su fecha.` };
      return { texto: p.motivo || `${p.nombre}: se omite.` };
    });
  }
  return filasPedidos(caso).map(f => {
    if (f.hecho) return { texto: f.motivo && f.motivo.includes('se omite') ? f.motivo : `${f.rol}: ya está liberada o finalizada, se omite.` };
    if (!f.sap) return { texto: `${f.rol}: sin pedido SAP asociado, se omite.` };
    return { texto: `SAP libera el pedido de ${f.rol.toLowerCase()}`, sap: f.sap };
  });
}
